import { useCallback } from 'react';
import {
  applySubstatusesToParentStatus,
  buildStatusRecordsFromField,
  buildStatusSavePayload,
  diffStatusRecords,
  getStatusId,
  getSyncableFields,
  mergeStatusOptionsFromApi,
  statusFieldHasOptions,
} from '../adapters/statusFieldSyncAdapter';
import statusWorkflowService from '../services/statusWorkflowService';
import { isStatusField, isSubStatusField } from '../utils/formStudioFormUtils';

async function fetchAllStatuses(formId) {
  const response = await statusWorkflowService.fetchStatuses({ leadFormId: formId });
  return Array.isArray(response?.data) ? response.data : [];
}

async function syncStatusField({ field, formId, workspaceId, allStatuses }) {
  if (!statusFieldHasOptions(field)) {
    return { skipped: true };
  }

  const existing = allStatuses.filter((item) => item?.type_of_status === field.key_name);
  const desired = buildStatusRecordsFromField(field);
  const { toUpsert, toDelete } = diffStatusRecords(existing, desired);
  const context = {
    campaignId: workspaceId,
    leadFormId: formId,
    timestamp: Date.now(),
  };

  for (const status of toDelete) {
    const statusId = getStatusId(status);
    if (!statusId) continue;
    const response = await statusWorkflowService.deleteStatus(statusId);
    if (response?.status !== 200) {
      throw new Error(`Failed to delete status "${status.status_name}".`);
    }
  }

  for (const status of toUpsert) {
    const payload = buildStatusSavePayload(status, context);
    const response = await statusWorkflowService.saveStatus(payload);
    if (response?.status !== 200) {
      throw new Error(`Failed to save status "${status.status_name}".`);
    }
  }

  return { success: true };
}

async function syncSubStatusField({ field, formId, workspaceId, allStatuses }) {
  if (!statusFieldHasOptions(field)) {
    return { skipped: true };
  }

  const parentKey = field.status_parent_key;
  if (!parentKey) {
    return { skipped: true };
  }

  const parentStatuses = allStatuses.filter((item) => item?.type_of_status === parentKey);
  if (!parentStatuses.length) {
    return { skipped: true };
  }

  const context = {
    campaignId: workspaceId,
    leadFormId: formId,
    timestamp: Date.now(),
  };

  for (const parentStatus of parentStatuses) {
    const updated = applySubstatusesToParentStatus(parentStatus, field, context.timestamp);
    const payload = buildStatusSavePayload(updated, context);
    const response = await statusWorkflowService.saveStatus(payload);
    if (response?.status !== 200) {
      throw new Error(`Failed to sync sub-statuses for "${parentStatus.status_name}".`);
    }
  }

  return { success: true };
}

export async function hydrateStatusOptionsFromApi(formId, statusField) {
  if (!formId || !isStatusField(statusField)) {
    return statusField;
  }

  const allStatuses = await fetchAllStatuses(formId);
  return mergeStatusOptionsFromApi(statusField, allStatuses);
}

export async function syncFieldStatuses({
  formId,
  workspaceId,
  fields,
  changedFieldKey,
} = {}) {
  if (!formId || !workspaceId) {
    return { success: true, skipped: true };
  }

  const syncableFields = getSyncableFields(fields || []);
  const targets = changedFieldKey
    ? syncableFields.filter((field) => field.key_name === changedFieldKey)
    : syncableFields;

  if (!targets.length) {
    return { success: true, skipped: true };
  }

  let allStatuses = await fetchAllStatuses(formId);

  for (const field of targets.filter(isStatusField)) {
    await syncStatusField({ field, formId, workspaceId, allStatuses });
  }

  allStatuses = await fetchAllStatuses(formId);

  for (const field of targets.filter(isSubStatusField)) {
    await syncSubStatusField({ field, formId, workspaceId, allStatuses });
  }

  return { success: true };
}

export default function useStatusFieldSync() {
  const syncFieldStatusesCallback = useCallback(syncFieldStatuses, []);
  const hydrateStatusOptionsFromApiCallback = useCallback(
    hydrateStatusOptionsFromApi,
    []
  );

  return {
    syncFieldStatuses: syncFieldStatusesCallback,
    hydrateStatusOptionsFromApi: hydrateStatusOptionsFromApiCallback,
  };
}
